import styles from "./PercentOverTimeGraph.module.css";
import PropTypes from "prop-types";
import classNames from "classnames";
import { memo } from "react";
import { formatPercent } from "../utils/formatPercent";
import { ArrowUp, ArrowDown, Close } from "react-ikonate";

// Chart size in pixels.
const CHART_HEIGHT = 30;
const CHART_WIDTH = 500;

// How far in from the bottom the lowest value starts, as a percentage of the height from 0 to 1.
const BOTTOM_PADDING = 0.15;

/**
 * Summarizes a set of monthly VCS stats as a horizontal chart.
 * Also displays the percentage change between the latest month and previous.
 */
export const PercentOverTimeGraph = memo(({ title, statName, data }) => {
  const availableHeight = CHART_HEIGHT * (1 - BOTTOM_PADDING);

  if (data == null || data.length < 2) {
    const polyPoints = [
      `${0} ${availableHeight}`,
      `${CHART_WIDTH} ${availableHeight}`,
      `${CHART_WIDTH} ${CHART_HEIGHT}`,
      `0 ${CHART_HEIGHT}`,
    ];

    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h4 className={styles.headerText}>{title}</h4>
        </header>

        <div className={styles.trendDisplay}>
          <Close className={classNames(styles.trendIcon, styles.noData)} />

          <span className={classNames(styles.trendLabel, styles.noData)}>
            ---
          </span>
        </div>

        <svg
          className={classNames(styles.chart, styles.noData)}
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
        >
          <polyline
            className={styles.chartLine}
            points={polyPoints.slice(0, polyPoints.length - 2).join(" ")}
          />
          <polygon className={styles.chartPoly} points={polyPoints.join(" ")} />
        </svg>
      </div>
    );
  } else {
    // console.log({ title, statName, data });

    // Extract data points and find the min and max.
    const points = data
      .map((x) => x)
      .sort((a, b) => (a.volumeMonth < b.volumeMonth ? -1 : 1))
      .map((record) => {
        return record[statName];
      });
    const highest = Math.max(...points);
    const lowest = Math.min(...points);

    // Find out where each point exists between the min and max.
    // We're going to map this onto the CHART_HEIGHT for drawing.
    const normalized = points.map(
      (point) => (point - lowest) / (highest - lowest)
    );

    // // Horizontal spacing between each point.
    const spacing = CHART_WIDTH / (points.length - 1);

    // Calculate SVG coordinates for the trend line.
    const lineCoords = normalized.map((position, i) => ({
      x: spacing * i,
      y: availableHeight - position * availableHeight,
    }));

    const polyPoints = lineCoords.map((coords) => `${coords.x} ${coords.y}`);

    // Add the two points to close the polygon.
    polyPoints.push(`${lineCoords[lineCoords.length - 1].x} ${CHART_HEIGHT}`); // bottom right
    polyPoints.push(`0 ${CHART_HEIGHT}`); // bottom left

    const dotPoints = lineCoords
      .slice(-2, lineCoords.length)
      .map((coords) => ({ cx: coords.x, cy: coords.y }));

    // console.log({
    //   normalized,
    //   first: normalized[normalized.length - 1],
    //   second: normalized[normalized.length - 2],
    //   resultRaw: 0 - 0.75,
    //   resultReal:
    //     normalized[normalized.length - 1] - normalized[normalized.length - 2],
    // });

    const len = normalized.length;

    // Calculate percentage trend between last two months.
    const trendAmount = normalized[len - 1] - normalized[len - 2];
    const trendPercent = formatPercent(trendAmount * 100);

    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h4 className={styles.headerText}>{title}</h4>
        </header>

        <div className={styles.trendDisplay}>
          {trendAmount > 0 ? (
            <ArrowUp className={classNames(styles.trendIcon, styles.trendUp)} />
          ) : (
            <ArrowDown
              className={classNames(styles.trendIcon, styles.trendDown)}
            />
          )}

          <span className={styles.trendLabel}>{trendPercent}</span>
        </div>

        <svg
          className={classNames(
            styles.chart,
            trendAmount > 0 ? styles.trendUp : styles.trendDown
          )}
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
        >
          <polyline
            className={styles.chartLine}
            points={polyPoints.slice(0, lineCoords.length).join(" ")}
          />
          <polygon className={styles.chartPoly} points={polyPoints.join(" ")} />

          <polyline
            className={styles.trendLine}
            points={polyPoints
              .slice(lineCoords.length - 2, lineCoords.length)
              .join(" ")}
          />
          <circle className={styles.trendDot} {...dotPoints[0]} r={3} />
          <circle className={styles.trendDot} {...dotPoints[1]} r={3} />
        </svg>
      </div>
    );
  }
});

PercentOverTimeGraph.propTypes = {
  /**
   * Title of the graph.
   */
  title: PropTypes.string.isRequired,

  /**
   * Stat to graph. This can be any of the numeric values in the data objects.
   */
  statName: PropTypes.oneOf([
    "personalVolume",
    "salesTotal",
    "orderTotal",
    "vcsPercentage",
  ]),

  /**
   * Source data.
   */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      volumeMonth: PropTypes.string.isRequired,
      personalVolume: PropTypes.number.isRequired,
      salesTotal: PropTypes.number.isRequired,
      orderTotal: PropTypes.number.isRequired,
      vcsPercentage: PropTypes.number.isRequired,
    })
  ).isRequired,
};
